import { type ReactElement, type ReactNode, useCallback, useRef, useState } from "react"

import type { CitationTextAnchor } from "@/utils/citation-anchor-utils"

import { RenderedPreviewBanner } from "@/components/rendered-preview-banner"
import { UnresolvedSourceViewer } from "@/features/file-viewer/components/unresolved-source-viewer"
import { extractCleanFilename } from "@/utils/file-utils"

import { CitationExtractedTextViewer } from "./citation-extracted-text-viewer"
import { HeavyPreviewGate } from "./heavy-preview-gate"
import { LazyPdfViewer } from "./lazy-pdf-viewer"
import { useCitation } from "../hooks/use-citation"
import { useHeavyPreviewGate } from "../hooks/use-heavy-preview-gate"
import {
  getHoverMiniRendererExtension,
  isHeavyPreviewExtension,
} from "../utils/citation-hover-mini-renderer"

type CitationViewerContentProps = {
  documentUrl: string
  textAnchor?: CitationTextAnchor | null
  /**
   * The citation's quote as written, before markdown normalization. The
   * extracted-text view checks it against the raw offset slice, which
   * `quoteSnippet` (markdown-stripped for PDF/DOCX highlighting) cannot match:
   * `file_name_v2` normalizes to `filenamev2`.
   */
  quote?: string | null
  quoteSnippet: string
  chunkSnippet: string
  /** null = no banner; extracted means the preview is derived text, while the original may be separately downloadable. */
  showModifiedPreviewBanner?: "converted" | "approximate" | "extracted" | null
  /**
   * Original artifact to render in place of `documentUrl`, set only when a
   * faithful viewer exists for its type. The derived view stays mounted as the
   * fallback for an original the backend cannot serve.
   */
  originalDocumentUrl?: string | null
  /**
   * The citation resolved to no delivered document. The pane names that instead
   * of requesting a locator, so no URL prop is read in this state.
   */
  isUnresolvedSource?: boolean
}

export function CitationViewerContent({
  documentUrl,
  textAnchor = null,
  quote = null,
  quoteSnippet,
  chunkSnippet,
  showModifiedPreviewBanner = null,
  originalDocumentUrl = null,
  isUnresolvedSource = false,
}: CitationViewerContentProps): ReactElement {
  const { renderComponent } = useCitation()
  const { heavyPreviewsGated, onSlowRender: scheduleHeavyPreviewAutoGate } = useHeavyPreviewGate()
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null)
  const setScrollContainerRef = useCallback((node: HTMLDivElement | null) => {
    setScrollContainer(node)
  }, [])

  const isHeavy = isHeavyPreviewExtension(documentUrl)

  // Capture whether gating was already active when this component first rendered
  // for this URL. If auto-gate triggers mid-render, we don't yank away the
  // preview the user is already reading — only future opens will be gated.
  const gatedOnMountRef = useRef(heavyPreviewsGated)
  const isGated = isHeavy && gatedOnMountRef.current

  // An unresolved citation carries no URL, and an empty one would select the
  // "file not found" viewer — a different, wrong answer. Build no viewer for it.
  let viewer: ReactNode = null
  if (!isUnresolvedSource) {
    viewer =
      showModifiedPreviewBanner === "extracted" && textAnchor ? (
        <CitationExtractedTextViewer
          fileUrl={documentUrl}
          quoteSnippet={quote ?? ""}
          scrollContainer={scrollContainer}
          textAnchor={textAnchor}
        />
      ) : (
        renderComponent(documentUrl, {
          quoteSnippet: quoteSnippet || null,
          chunkSnippet: chunkSnippet || null,
          highlightScrollContainer: scrollContainer,
          onSlowRender: isHeavy && !heavyPreviewsGated ? scheduleHeavyPreviewAutoGate : undefined,
        })
      )
  }

  const fileName = extractCleanFilename(documentUrl)
  const fileExtension = getHoverMiniRendererExtension(documentUrl)

  const derivedView = (
    <>
      {showModifiedPreviewBanner != null ? (
        <RenderedPreviewBanner
          kind={showModifiedPreviewBanner === "extracted" ? "extracted" : "rendered"}
        />
      ) : null}
      {isGated && viewer ? (
        <HeavyPreviewGate fileExtension={fileExtension} fileName={fileName}>
          {viewer}
        </HeavyPreviewGate>
      ) : (
        viewer
      )}
    </>
  )

  let pane: ReactNode
  if (isUnresolvedSource) {
    pane = <UnresolvedSourceViewer />
  } else if (originalDocumentUrl) {
    // The original renders without the derived-preview banner: this is the
    // cited file itself. `derivedView` is only an element here — nothing
    // fetches until the PDF load fails and it actually mounts.
    pane = (
      <LazyPdfViewer
        fileUrl={originalDocumentUrl}
        highlightChunk={chunkSnippet || null}
        highlightQuote={quoteSnippet || null}
        highlightScrollContainer={scrollContainer}
        unavailableFallback={derivedView}
      />
    )
  } else {
    pane = derivedView
  }

  return (
    <div ref={setScrollContainerRef} className="h-full min-h-0 overflow-auto p-4">
      {pane}
    </div>
  )
}
